/**
 * OdinMascot — Norse-mechanical humanoid portrait of oDIN.
 *
 * Pure inline SVG. No external libraries. Animates via CSS classes.
 * Respects prefers-reduced-motion.
 *
 * Props:
 *   state    'idle' | 'thinking' | 'responding' | 'listening'
 *   size     number — the width in px; height scales with variant
 *   variant  'portrait' (default, head+collar) | 'bust' (head+shoulders+chest)
 *   className  string — additional Tailwind/CSS classes
 */
export function OdinMascot({ state = 'idle', size = 80, variant = 'portrait', className = '' }) {
  const isBust = variant === 'bust';
  // portrait: 80×96 (ratio 1.2); bust: 80×148 (ratio 1.85)
  const viewH = isBust ? 148 : 96;
  const h = Math.round(size * (viewH / 80));
  return (
    <svg
      viewBox={`0 0 80 ${viewH}`}
      width={size}
      height={h}
      xmlns="http://www.w3.org/2000/svg"
      className={`odin-mascot odin-mascot--${state} ${className}`}
      aria-label="oDIN"
      role="img"
    >
      <defs>
        {/* Soft violet glow for the eye */}
        <filter id="odin-eye-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Faint rune glow */}
        <filter id="odin-rune-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Eye iris gradient */}
        <radialGradient id="odin-iris" cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor="hsl(263 95% 82%)" />
          <stop offset="50%" stopColor="hsl(263 80% 62%)" />
          <stop offset="100%" stopColor="hsl(263 60% 28%)" />
        </radialGradient>

        {/* Helmet body gradient (top-left light) */}
        <linearGradient id="odin-helm" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#252030" />
          <stop offset="100%" stopColor="#16131f" />
        </linearGradient>

        {/* Cheek guard gradient */}
        <linearGradient id="odin-cheek-l" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1e1a2a" />
          <stop offset="100%" stopColor="#252030" />
        </linearGradient>
        <linearGradient id="odin-cheek-r" x1="1" y1="0" x2="0" y2="0">
          <stop offset="0%" stopColor="#1e1a2a" />
          <stop offset="100%" stopColor="#252030" />
        </linearGradient>
      </defs>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* HELMET MAIN BODY                                           */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Outer helmet silhouette */}
      <path
        d="M26 10 L54 10 L67 23 L69 54 L61 75 L40 82 L19 75 L11 54 L13 23 Z"
        fill="url(#odin-helm)"
        stroke="#3e3a52"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />

      {/* Helmet top crest (central vertical fin) */}
      <path
        d="M36 10 L40 3 L44 10"
        fill="#1e1a2a"
        stroke="#5a566e"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Crest edge highlight */}
      <line x1="40" y1="3" x2="40" y2="10"
        stroke="#7c77a0"
        strokeWidth="0.6"
        opacity="0.7"
      />

      {/* Forehead central plate */}
      <path
        d="M33 10 L37 22 L40 24 L43 22 L47 10"
        fill="#201d2e"
        stroke="#3e3a52"
        strokeWidth="0.9"
      />

      {/* Left cheek guard */}
      <path
        d="M13 23 L22 20 L25 38 L19 58 L11 54 Z"
        fill="url(#odin-cheek-l)"
        stroke="#3e3a52"
        strokeWidth="1"
      />

      {/* Right cheek guard */}
      <path
        d="M67 23 L58 20 L55 38 L61 58 L69 54 Z"
        fill="url(#odin-cheek-r)"
        stroke="#3e3a52"
        strokeWidth="1"
      />

      {/* Edge highlight — left rim */}
      <path
        d="M13 23 L22 20"
        fill="none"
        stroke="#6b6680"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      {/* Edge highlight — right rim */}
      <path
        d="M67 23 L58 20"
        fill="none"
        stroke="#6b6680"
        strokeWidth="1.2"
        strokeLinecap="round"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* BROW RIDGE                                                 */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Main brow ridge bar */}
      <path
        d="M16 35 L28 27 L38 26 L42 26 L52 27 L64 35"
        fill="none"
        stroke="#6b6680"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      {/* Brow underside shadow */}
      <path
        d="M18 36 L30 29 L40 28 L50 29 L62 36"
        fill="none"
        stroke="#100d1a"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.7"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* LEFT EYE — open, violet, otherworldly                      */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Socket / recess */}
      <path
        d="M18 33 L30 27 L38 31 L36 43 L26 46 L17 41 Z"
        fill="#0e0b18"
        stroke="#2e2a3e"
        strokeWidth="1"
      />

      {/* Iris glow — large, animated */}
      <ellipse
        cx="27.5"
        cy="36.5"
        rx="7"
        ry="6.5"
        fill="url(#odin-iris)"
        filter="url(#odin-eye-glow)"
        className="odin-eye-iris"
      />

      {/* Iris texture ring */}
      <ellipse
        cx="27.5"
        cy="36.5"
        rx="5"
        ry="4.5"
        fill="none"
        stroke="hsl(263 85% 78%)"
        strokeWidth="0.5"
        opacity="0.45"
      />

      {/* Vertical slit pupil (otherworldly) */}
      <ellipse
        cx="27.5"
        cy="36.5"
        rx="1.3"
        ry="3"
        fill="#080613"
      />

      {/* Eye highlight (top-left reflection) */}
      <ellipse
        cx="25.5"
        cy="33.5"
        rx="1.4"
        ry="1"
        fill="white"
        fillOpacity="0.65"
      />

      {/* Eye corner energy traces */}
      <line x1="17" y1="37" x2="20.5" y2="37"
        stroke="hsl(263 70% 60%)"
        strokeWidth="0.8"
        opacity="0.5"
        className="odin-eye-trace"
      />
      <line x1="34.5" y1="37" x2="38" y2="37"
        stroke="hsl(263 70% 60%)"
        strokeWidth="0.8"
        opacity="0.5"
        className="odin-eye-trace"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* EYEPATCH — right eye (Odin's sacrifice)                    */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Patch outer shadow */}
      <path
        d="M42 29 L55 26 L63 33 L61 45 L51 49 L42 43 Z"
        fill="#0a0812"
        stroke="#2e2a3e"
        strokeWidth="1"
      />

      {/* Patch body — angular metal plate */}
      <path
        d="M43 30 L55 27 L62 34 L60 44 L51 48 L43 42 Z"
        fill="#181424"
        stroke="#3e3a52"
        strokeWidth="1"
      />

      {/* Patch subtle surface texture (two parallel lines) */}
      <line x1="45" y1="34" x2="55" y2="31"
        stroke="#2e2a3e"
        strokeWidth="0.7"
        opacity="0.6"
      />
      <line x1="45" y1="40" x2="55" y2="37"
        stroke="#2e2a3e"
        strokeWidth="0.7"
        opacity="0.6"
      />

      {/* Vegvísir-inspired eight-armed sigil on the patch */}
      <g
        filter="url(#odin-rune-glow)"
        stroke="hsl(263 70% 58%)"
        strokeWidth="0.75"
        fill="none"
        opacity="0.6"
        className="odin-patch-rune"
      >
        {/* Center point */}
        <circle cx="52.5" cy="37.5" r="1" fill="hsl(263 70% 58%)" stroke="none" />
        {/* Eight arms (compass + diagonals) */}
        <line x1="52.5" y1="37.5" x2="52.5" y2="31.5" />  {/* N  */}
        <line x1="52.5" y1="37.5" x2="52.5" y2="43.5" />  {/* S  */}
        <line x1="52.5" y1="37.5" x2="46.5" y2="37.5" />  {/* W  */}
        <line x1="52.5" y1="37.5" x2="58.5" y2="37.5" />  {/* E  */}
        <line x1="52.5" y1="37.5" x2="48.3" y2="33.3" />  {/* NW */}
        <line x1="52.5" y1="37.5" x2="56.7" y2="33.3" />  {/* NE */}
        <line x1="52.5" y1="37.5" x2="48.3" y2="41.7" />  {/* SW */}
        <line x1="52.5" y1="37.5" x2="56.7" y2="41.7" />  {/* SE */}
      </g>

      {/* Eyepatch strap — upper right */}
      <path
        d="M62 34 L66 28 L68 25"
        fill="none"
        stroke="#3e3a52"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {/* Eyepatch strap — lower left (toward other side of head) */}
      <path
        d="M43 41 L39 48 L37 53"
        fill="none"
        stroke="#3e3a52"
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* NOSE BRIDGE                                                */}
      {/* ═══════════════════════════════════════════════════════════ */}

      <path
        d="M37 44 L38 52 L40 58 L42 52 L43 44"
        fill="none"
        stroke="#4a4660"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* LOWER FACE                                                 */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Lower face plate seam */}
      <path
        d="M20 58 L29 63 L36 65 L40 66 L44 65 L51 63 L60 58"
        fill="none"
        stroke="#3e3a52"
        strokeWidth="1"
        strokeLinecap="round"
      />

      {/* Mouth — closed, slightly grim, characteristic */}
      <path
        d="M33 61 L37 62.5 L40 62 L43 62.5 L47 61"
        fill="none"
        stroke="#5a566e"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Chin definition */}
      <path
        d="M26 68 L32 72 L36 75 L40 76 L44 75 L48 72 L54 68"
        fill="none"
        stroke="#2e2a3e"
        strokeWidth="1"
        strokeLinecap="round"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* RUNE ENGRAVINGS (cheeks + forehead)                        */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Left cheek — Othala rune ᛟ (Odin's rune, inheritance, home) */}
      <g
        filter="url(#odin-rune-glow)"
        stroke="hsl(263 70% 60%)"
        strokeWidth="0.9"
        fill="none"
        opacity="0.45"
        className="odin-runes"
      >
        <path d="M15 44 L15 53" />
        <path d="M12 46 L15 43 L18 46" />
        <path d="M12 51 L15 54 L18 51" />
        <path d="M12 46 L12 51" />
        <path d="M18 46 L18 51" />
      </g>

      {/* Right cheek — Ansuz rune ᚨ (communication, magic, oDIN's breath) */}
      <g
        filter="url(#odin-rune-glow)"
        stroke="hsl(263 70% 60%)"
        strokeWidth="0.9"
        fill="none"
        opacity="0.45"
        className="odin-runes"
      >
        <path d="M63 43 L63 54" />
        <path d="M63 47 L67 43" />
        <path d="M63 51 L67 47" />
      </g>

      {/* Forehead — small diamond rune (Isa/silence, stylised) */}
      <path
        d="M38 16 L40 13 L42 16 L40 19 Z"
        fill="none"
        stroke="hsl(263 70% 60%)"
        strokeWidth="0.75"
        opacity="0.4"
        filter="url(#odin-rune-glow)"
        className="odin-runes"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* NECK & COLLAR                                              */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {/* Neck */}
      <path
        d="M30 76 L32 84 L40 88 L48 84 L50 76"
        fill="#181424"
        stroke="#2e2a3e"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />

      {/* Collar / gorget — angular, mechanical */}
      <path
        d="M20 82 L25 91 L40 95 L55 91 L60 82 L53 78 L40 80 L27 78 Z"
        fill="#1a1626"
        stroke="#3e3a52"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />

      {/* Collar highlight edge */}
      <path
        d="M20 82 L25 78"
        fill="none"
        stroke="#5a566e"
        strokeWidth="0.8"
        strokeLinecap="round"
      />
      <path
        d="M60 82 L55 78"
        fill="none"
        stroke="#5a566e"
        strokeWidth="0.8"
        strokeLinecap="round"
      />

      {/* Collar rune trace line */}
      <path
        d="M29 88 L40 86 L51 88"
        fill="none"
        stroke="hsl(263 70% 58%)"
        strokeWidth="0.75"
        opacity="0.5"
        filter="url(#odin-rune-glow)"
        className="odin-runes"
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* BUST VARIANT — shoulders + chest (only rendered for bust)  */}
      {/* ═══════════════════════════════════════════════════════════ */}

      {isBust && <>
        {/* Left pauldron */}
        <path
          d="M20 84 L8 90 L4 105 L8 120 L24 126 L38 120 L40 104 L30 90 Z"
          fill="url(#odin-helm)"
          stroke="#3e3a52"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        {/* Left pauldron edge highlight */}
        <path
          d="M8 90 L4 105"
          fill="none"
          stroke="#6b6680"
          strokeWidth="1"
          strokeLinecap="round"
        />
        {/* Left pauldron grooves */}
        <path d="M9 98 L22 100" fill="none" stroke="#2e2a3e" strokeWidth="0.8" opacity="0.7" />
        <path d="M10 108 L24 112" fill="none" stroke="#2e2a3e" strokeWidth="0.8" opacity="0.7" />
        {/* Left pauldron rune — Elhaz ᛉ (protection) */}
        <g
          filter="url(#odin-rune-glow)"
          stroke="hsl(263 70% 60%)"
          strokeWidth="0.8"
          fill="none"
          opacity="0.4"
          className="odin-runes"
        >
          <line x1="14" y1="103" x2="14" y2="114" />
          <line x1="14" y1="106" x2="11" y2="103" />
          <line x1="14" y1="106" x2="17" y2="103" />
        </g>

        {/* Right pauldron */}
        <path
          d="M60 84 L72 90 L76 105 L72 120 L56 126 L42 120 L40 104 L50 90 Z"
          fill="url(#odin-helm)"
          stroke="#3e3a52"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        {/* Right pauldron edge highlight */}
        <path
          d="M72 90 L76 105"
          fill="none"
          stroke="#6b6680"
          strokeWidth="1"
          strokeLinecap="round"
        />
        {/* Right pauldron grooves */}
        <path d="M71 98 L58 100" fill="none" stroke="#2e2a3e" strokeWidth="0.8" opacity="0.7" />
        <path d="M70 108 L56 112" fill="none" stroke="#2e2a3e" strokeWidth="0.8" opacity="0.7" />
        {/* Right pauldron rune — Tiwaz ᛏ (victory, justice) */}
        <g
          filter="url(#odin-rune-glow)"
          stroke="hsl(263 70% 60%)"
          strokeWidth="0.8"
          fill="none"
          opacity="0.4"
          className="odin-runes"
        >
          <line x1="66" y1="103" x2="66" y2="114" />
          <line x1="66" y1="103" x2="63" y2="107" />
          <line x1="66" y1="103" x2="69" y2="107" />
        </g>

        {/* Central chest plate */}
        <path
          d="M34 118 L40 114 L46 118 L48 136 L40 142 L32 136 Z"
          fill="#181424"
          stroke="#3e3a52"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        {/* Chest plate — central edge highlights */}
        <path d="M34 118 L32 136" fill="none" stroke="#4a4660" strokeWidth="0.7" />
        <path d="M46 118 L48 136" fill="none" stroke="#4a4660" strokeWidth="0.7" />
        {/* Chest rune — Ingwaz ◇ (fertility, peace, inner strength) */}
        <g
          filter="url(#odin-rune-glow)"
          stroke="hsl(263 70% 60%)"
          strokeWidth="0.9"
          fill="none"
          opacity="0.55"
          className="odin-runes"
        >
          <path d="M40 120 L43 124 L40 128 L37 124 Z" />
        </g>

        {/* Chest plate collar seam */}
        <path
          d="M30 115 L40 112 L50 115"
          fill="none"
          stroke="hsl(263 70% 58%)"
          strokeWidth="0.75"
          opacity="0.45"
          filter="url(#odin-rune-glow)"
          className="odin-runes"
        />
      </>}
    </svg>
  );
}

export default OdinMascot;
